import type { ReactNode } from 'react';

/** Uma fonte citada num texto da Machine. `n` é o número que aparece pequeno no texto; `label` diz o que é; `detail`, o trecho. */
export interface CitedRef { n: number; label: string; detail?: string | null; href?: string | null }

const MARK = /\{\{(\d+)\}\}/g;

/**
 * Texto da Machine com citações. Os marcadores `{{n}}` viram números pequenos que levam à lista de fontes (`CitedSources`
 * com o mesmo `scope`). Vários seguidos ficam juntos. Marcador de fonte que não está em `refs` some: nunca aparece código
 * nem nome de fonte no meio da frase. Serve aos dois temas (cor por token). É o mesmo desenho da resposta da Memória.
 */
export function Cited({ text, refs, scope }: { text: string; refs: CitedRef[]; scope: string }) {
  const out: ReactNode[] = [];
  let last = 0;
  let group: number[] = [];
  const flush = (key: string) => {
    if (!group.length) return;
    out.push(<sup key={key} className="rr-cited__refs">{group.map((n) => <a key={n} href={`#cite-${scope}-${n}`} title={refs.find((r) => r.n === n)?.label}>{n}</a>)}</sup>);
    group = [];
  };
  for (const m of text.matchAll(MARK)) {
    const before = text.slice(last, m.index);
    if (before.trim()) { flush(`r${last}`); out.push(before.replace(/\s+$/, '')); }
    const n = Number(m[1]);
    if (refs.some((r) => r.n === n) && !group.includes(n)) group.push(n);
    last = m.index! + m[0].length;
  }
  flush(`r${last}`);
  const rest = text.slice(last);
  if (rest.trim()) out.push(rest);
  return <>{out}</>;
}

/** A lista "de onde vem" de um texto com citações. */
export function CitedSources({ refs, scope, title = 'De onde vem' }: { refs: CitedRef[]; scope: string; title?: string }) {
  if (refs.length === 0) return null;
  return (
    <div className="rr-cited">
      <span className="rr-cited__title">{title}</span>
      <ol className="rr-cited__list">
        {refs.map((r) => (
          <li key={r.n} id={`cite-${scope}-${r.n}`} value={r.n}>
            {r.href ? <a href={r.href} target="_blank" rel="noreferrer">{r.label}</a> : <strong>{r.label}</strong>}
            {r.detail && <span>{r.detail}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}
